import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { getService, SERVICES } from "../data/services";
import { REALISATIONS } from "../data/realisations";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal, Label } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const SITE_URL = "https://resineko.com";

const ServicePage = ({ slug }) => {
  const service = getService(slug);
  if (!service) return null;
  const chantiers = REALISATIONS.filter((r) => r.serviceSlug === slug && !r.illustration).slice(0, 3);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: service.name,
        provider: { "@type": "Organization", name: "Resineko", url: SITE_URL },
        areaServed: "France",
        description: service.metaDescription,
      },
      {
        "@type": "FAQPage",
        mainEntity: service.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <>
      <Seo title={service.metaTitle} description={service.metaDescription} path={`/${service.slug}`} jsonLd={jsonLd} />
      <PageHero kicker="Nos solutions" title={service.title} subtitle={service.tagline} image={service.image} />

      {/* Intro + applications */}
      <section className="noise-bg bg-[#0A0A0A] py-20 md:py-32">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 md:grid-cols-12 md:px-10">
          <div className="md:col-span-7">
            <Reveal><Label>{service.name}</Label></Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 font-serif text-2xl font-light leading-snug text-[#F4F4F0]/90 md:text-3xl">
                {service.intro}
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <Reveal delay={0.15}>
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#8C8C8C]">Applications</p>
              <ul className="mt-5 space-y-2.5" data-testid="service-applications">
                {service.applications.map((a) => (
                  <li key={a} className="flex items-center gap-3 text-sm text-[#F4F4F0]/80">
                    <Check size={14} className="shrink-0 text-[#2FBF8F]" /> {a}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Sections éditoriales */}
      <section className="bg-[#0e0e0e] py-20 md:py-32">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <div className="space-y-20">
            {service.sections.map((sec, i) =>
              sec.noImage ? (
                <Reveal key={sec.title}>
                  <div className="mx-auto max-w-3xl text-center">
                    <span className="font-serif text-lg italic text-[#047857]">{String(i + 1).padStart(2, "0")}</span>
                    <h2 className="mt-4 font-serif text-3xl font-light leading-tight text-[#F4F4F0] md:text-4xl">{sec.title}</h2>
                    <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-[#8C8C8C] md:text-base">{sec.text}</p>
                  </div>
                </Reveal>
              ) : (
              <div key={sec.title} className={`grid items-center gap-10 md:grid-cols-2 ${i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`}>
                <Reveal>
                  <span className="font-serif text-lg italic text-[#047857]">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="mt-4 font-serif text-3xl font-light leading-tight text-[#F4F4F0] md:text-4xl">{sec.title}</h2>
                  <p className="mt-5 max-w-lg text-sm leading-relaxed text-[#8C8C8C] md:text-base">{sec.text}</p>
                </Reveal>
                <Reveal delay={0.1}>
                  <div className="group overflow-hidden border border-white/10">
                    <img
                      src={sec.image || service.gallery[i % service.gallery.length]}
                      alt={`${service.name} — ${sec.title}`}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                </Reveal>
              </div>
              )
            )}
          </div>
        </div>
      </section>

      {/* Points clés */}
      <section className="bg-[#F4F4F0] py-20 text-[#0A0A0A] md:py-32">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal><Label light>Les points clés</Label></Reveal>
          <div className="mt-12 grid gap-px overflow-hidden border border-black/10 bg-black/10 sm:grid-cols-2 lg:grid-cols-4">
            {service.points.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <div className="h-full bg-[#F4F4F0] p-8 transition-colors duration-500 hover:bg-[#ecece6]">
                  <h3 className="font-serif text-xl font-normal">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#0A0A0A]/60">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Galerie */}
      <section className="bg-[#0A0A0A] py-20 md:py-32">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal><Label>Galerie</Label></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-3xl font-light text-[#F4F4F0] md:text-4xl">{service.name}</h2>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {service.gallery.map((img, i) => (
              <Reveal key={img} delay={i * 0.06}>
                <div className="group overflow-hidden border border-white/10">
                  <img src={img} alt={`${service.name} — visuel ${i + 1}`} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Chantiers récents de ce savoir-faire */}
      {chantiers.length > 0 && (
        <section className="border-t border-white/10 bg-[#0e0e0e] py-20 md:py-32" data-testid="service-chantiers">
          <div className="mx-auto max-w-7xl px-5 md:px-10">
            <Reveal><Label>Chantiers récents</Label></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-3xl font-light text-[#F4F4F0] md:text-4xl">
                Réalisations Resineko — {service.name.toLowerCase()}
              </h2>
            </Reveal>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {chantiers.map((r, i) => (
                <Reveal key={r.slug} delay={i * 0.06}>
                  <Link to={`/realisations/${r.slug}`} data-testid={`service-chantier-${r.slug}`} className="group block border border-white/10 transition-colors duration-300 hover:border-[#047857]">
                    <div className="overflow-hidden">
                      <img src={r.image} alt={r.description} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="p-5">
                      <p className="text-[10px] uppercase tracking-[0.25em] text-[#2FBF8F]">Réalisation Resineko — {r.ville}</p>
                      <p className="mt-2 font-serif text-lg font-normal text-[#F4F4F0]">{r.espace}</p>
                      <p className="mt-3 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#8C8C8C] transition-colors group-hover:text-[#2FBF8F]">
                        Voir le chantier <ArrowRight size={12} />
                      </p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="bg-[#0e0e0e] py-20 md:py-32" data-testid="service-faq">
        <div className="mx-auto max-w-4xl px-5 md:px-10">
          <Reveal><Label>Questions fréquentes</Label></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-3xl font-light text-[#F4F4F0] md:text-4xl">Tout savoir sur la {service.name.toLowerCase()}</h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Accordion type="single" collapsible className="mt-10">
              {service.faq.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                  <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-serif text-lg font-normal text-[#F4F4F0] hover:text-[#2FBF8F] hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-[#8C8C8C]">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Autres solutions */}
      <section className="border-t border-white/10 bg-[#0A0A0A] py-16">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#8C8C8C]">Découvrir aussi</p>
          <div className="mt-6 flex flex-wrap gap-3">
            {SERVICES.filter((s) => s.slug !== slug).map((s) => (
              <Link
                key={s.slug}
                to={`/${s.slug}`}
                data-testid={`related-${s.slug}`}
                className="group inline-flex items-center gap-2 border border-white/15 px-5 py-3 text-sm text-[#F4F4F0]/80 transition-colors duration-300 hover:border-[#047857] hover:text-[#2FBF8F]"
              >
                {s.name}
                <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            ))}
            {slug === "peinture-batiment" && (
              <Link
                to="/peinture-toulouse"
                data-testid="related-peinture-toulouse"
                className="group inline-flex items-center gap-2 border border-[#047857]/50 px-5 py-3 text-sm text-[#2FBF8F] transition-colors duration-300 hover:border-[#047857]"
              >
                Peintre en bâtiment à Toulouse
                <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            )}
          </div>
        </div>
      </section>

      <CTASection cta={service.cta} />
    </>
  );
};

export default ServicePage;
